import {
  flowMax,
  addDisplayName,
  addProps,
  addDefaultProps,
  addWrapper,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Edit, Tag } from 'shared/src/api/types'
import { Link as RouterLink } from 'react-router-dom'
import cx from 'classnames'
import { addEffectOnMount } from 'ad-hok-utils'
import { scrollToTab } from 'shared/src/utils/scrollToId'

import { makeEditFeedUrl, makeTagFeedUrl } from 'utils/routes'
import { sansSerifFontFamily } from 'utils/styles'
import { TABS_Z_INDEX } from 'utils/zIndex'
import { LAYOUT_MAX_WIDTH } from 'utils/layout'

const useStyles = createUseStyles({
  container: {
    position: 'fixed',
    top: 50,
    zIndex: TABS_Z_INDEX,
    maxWidth: `${LAYOUT_MAX_WIDTH}px`,
    width: `100vw`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  link: {
    fontFamily: sansSerifFontFamily,
    fontWeight: 400,
    textDecoration: 'none',
    flexShrink: 0,
    paddingTop: 8,
    paddingBottom: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    gap: 4,
  },
  selectedLink: {
    border: 'none',
    background: 'none',
    color: '#fff',
    fontWeight: 700,
    padding: 0,
  },
  unselectedLink: {
    color: '#797979',
  },
  italic: {
    fontStyle: 'italic',
  },
  chevron: {
    marginBottom: -3,
  },
})

export interface TabData {
  displayName: string
  path: string
  feedId: string | number
}

interface Tags {
  tags: Tag[]
}

interface Edits {
  edits: Edit[]
}

type Data = Tags | Edits

const isTags = (data: Data): data is Tags => 'tags' in data

const dataToTabs = (data: Data): TabData[] =>
  isTags(data)
    ? data.tags.map(({ id, name }) => ({
        displayName: name,
        path: makeTagFeedUrl(id),
        feedId: id,
      }))
    : data.edits.map(({ id, name }) => ({
        displayName: name,
        path: makeEditFeedUrl(id),
        feedId: id,
      }))

interface TabProps {
  tab: TabData
  currentFeedId: string | number
  onClickCurrentTab?: () => void
}

const Tab: FC<TabProps> = flowMax(
  addDisplayName('Tab'),
  addProps(({ currentFeedId, tab: { feedId } }) => ({
    isCurrentTab: currentFeedId.toString() === feedId.toString(),
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addWrapper(
    (
      render,
      { tab: { feedId, path }, isCurrentTab, onClickCurrentTab, classes }
    ) =>
      isCurrentTab ? (
        <button
          onClick={onClickCurrentTab}
          className={cx(classes.link, classes.selectedLink)}
        >
          {render()}
        </button>
      ) : (
        <RouterLink
          id={feedId.toString()}
          to={path}
          replace
          className={cx(classes.link, classes.unselectedLink)}
        >
          {render()}
        </RouterLink>
      )
  ),
  ({ tab: { feedId, displayName } }) => (
    <div id={feedId.toString()}>{displayName}</div>
  )
)

interface Props {
  extraTabs?: TabData[]
  data: Data
  className?: string
  currentFeedId: string | number
  onClickCurrentTab?: () => void
}

export const Tabs: FC<Props> = flowMax(
  addDisplayName('Tabs'),
  addDefaultProps({
    extraTabs: [],
  }),
  addProps(({ extraTabs, data }) => ({
    tabs: [...extraTabs, ...dataToTabs(data)],
  })),
  addEffectOnMount(({ currentFeedId }) => () => {
    scrollToTab(currentFeedId.toString())
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ tabs, currentFeedId, onClickCurrentTab, classes, className }) => (
    <div className={cx(classes.container, className)}>
      {tabs.map((tab, index) => (
        <Tab
          tab={tab}
          key={index}
          currentFeedId={currentFeedId}
          onClickCurrentTab={onClickCurrentTab}
        />
      ))}
    </div>
  )
)
